import { toast } from "sonner";
import { splitUpload, patchUpload, pinUploadPage } from "../../lib/api";

/**
 * Workspace file-actions hook — pulls the toggle/split/pin/rename
 * handlers out of WorkspacePage so the shell stays focused on layout
 * (iter-104 refactor).
 *
 * Every handler is optimistic-where-possible and falls back to a full
 * `loadAll()` reload on server failure to restore canonical state.
 *
 * Args bundle is intentionally tiny — all setters the handlers need
 * plus `splittingIds` (for re-entry guarding) and `loadAll`.
 */
export function useFileActions({
    setFiles,
    setExpandedPdfs,
    splittingIds,
    setSplittingIds,
    setPinningSubpage,
    setActiveFileId,
    setActivePage,
    loadAll,
}) {
    const togglePdfExpanded = (id) => {
        setExpandedPdfs((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    };

    const handleSplitUpload = async (f) => {
        if (splittingIds.has(f.id)) return;
        setSplittingIds((prev) => new Set(prev).add(f.id));
        try {
            const res = await splitUpload(f.id);
            if (res.already_split) {
                toast.message("Already split", {
                    description: `This file has ${res.children} pages on disk already.`,
                });
            } else {
                toast.success(`Split into ${res.children} pages`);
            }
            await loadAll();
            setExpandedPdfs((prev) => new Set(prev).add(f.id));
        } catch (err) {
            const msg = err?.response?.data?.detail || err.message || "Split failed";
            toast.error(msg);
        } finally {
            setSplittingIds((prev) => {
                const next = new Set(prev);
                next.delete(f.id);
                return next;
            });
        }
    };

    const togglePin = async (f) => {
        const nextPinned = !f.pinned;
        // Optimistic update with re-sort so pinned files float to the top
        // immediately (stable order is preserved otherwise).
        setFiles((prev) => {
            const updated = prev.map((x) =>
                x.id === f.id ? { ...x, pinned: nextPinned } : x,
            );
            return [
                ...updated.filter((x) => x.pinned),
                ...updated.filter((x) => !x.pinned),
            ];
        });
        try {
            await patchUpload(f.id, { pinned: nextPinned });
        } catch {
            toast.error("Couldn't update pin");
            loadAll();
        }
    };

    const handlePinSubpage = async (parentFile, pageNumber) => {
        setPinningSubpage({ fileId: parentFile.id, page: pageNumber });
        try {
            const child = await pinUploadPage(parentFile.id, pageNumber);
            toast.success(`Pinned page ${pageNumber}`);
            // Refresh so the new child surfaces and the parent's
            // expansion switches from synthetic sub-pages to real
            // children.
            await loadAll();
            // Select the newly pinned child so the user sees the exact page.
            if (child?.id) {
                setActiveFileId(child.id);
                setActivePage(1);
            }
        } catch (err) {
            const detail = err?.response?.data?.detail;
            toast.error(typeof detail === "string" ? detail : "Couldn't pin page");
        } finally {
            setPinningSubpage({ fileId: null, page: null });
        }
    };

    const handleRenameUpload = async (file, newName) => {
        const trimmed = (newName || "").trim();
        // Optimistic update so the rename feels instant.
        setFiles((prev) =>
            prev.map((x) =>
                x.id === file.id ? { ...x, display_name: trimmed } : x,
            ),
        );
        try {
            await patchUpload(file.id, { display_name: trimmed });
        } catch {
            toast.error("Couldn't rename");
            loadAll();
            throw new Error("rename-failed");
        }
    };

    return {
        togglePdfExpanded,
        handleSplitUpload,
        togglePin,
        handlePinSubpage,
        handleRenameUpload,
    };
}
